<template>
  <Layout>
    <div class="tournament-wrapper">
      <StellarBackground backColor="#2d3434"/>
      <div class="tournament-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="tournament-page" :class="{ 'content-loaded': !isLoading }">
            <TournamentDetail 
              :tournament="Tournaments[tournamentIndex]" 
              @register="handleRegistration"
            />
            <div class="bottom-links d-none d-md-flex">
              <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
              <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
              <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
            </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import Layout from '@/components/layout/Layout.vue'
import StellarBackground from '@/components/animations/StellarBackground.vue'
import CyberLoading from '@/components/ui/CyberLoading.vue'
import TournamentDetail from './TournamentDetail.vue'

const { t, tm } = useI18n()
const route = useRoute()
const isLoading = ref(true)

const handleRegistration = (tournamentId: number) => {
  console.log('Registration requested for tournament:', tournamentId)
  // Add your registration logic here
}

const formatUrlSlug = (title: string): string => {
  return title.toLowerCase().replace(/\s+/g, '-');
};

let tournamentIndex = computed(() => {
  const urlSlug = route.params.title as string;
  return Tournaments.value.findIndex(tournament => 
    formatUrlSlug(tournament.title) === urlSlug
  );
})

onMounted(() => {
  // Simulate loading time for dramatic effect
  setTimeout(() => {
    isLoading.value = false
  }, 1300)
})

import chessBg from '@/assets/backgrounds/Chess - Fun.webp'
import chickenNuggetBg from '@/assets/backgrounds/Chicken Nuggets - Fun.webp'
import CS2Bg from '@/assets/backgrounds/Counter Strike 2 - Grand.webp'
import lolGrandBg from '@/assets/backgrounds/League of Legends - Grand.webp'
import lolNormalBg from '@/assets/backgrounds/League of Legends - Normal.webp'
import lolSoloBg from '@/assets/backgrounds/League of Legends - Solo.webp'
import marvelRivalBg from '@/assets/backgrounds/Marvel Rivals - Normal.webp'
import rocketLeagueBg from '@/assets/backgrounds/Rocket League - Normal.webp'
import rocketLeagueSoloBg from '@/assets/backgrounds/Rocket League - Solo.webp'
import tftBg from '@/assets/backgrounds/Teamfight Tactics - Solo.webp'
import trackManiaBg from '@/assets/backgrounds/Trackmania - Solo.webp'
import quizBg from '@/assets/backgrounds/Quiz - Fun.webp'
import CS2CoreBG from '@/assets/backgrounds/Counter Strike 2 - Core.webp'

interface TournamentDetail {
  id: number
  title: string
  date: string
  startTime: string
  endTime: string
  description: string
  image: string
  maxParticipants: string
  prizePool: string
  category: 'prime' | 'core' | 'solo' | 'fun'
  maps?: string[]
  rules: string[]
  disclaimer?: string[]
  inGame: string[]
  customization: string[]
  gameclient: string[]
  schedule: string[]
  urlSignUp: string
  urlBracket: string
  prizes: string[]
  scoring: string[]
  registrationOpen: boolean
}

const Tournaments = computed<TournamentDetail[]>(() => [
  {
    id: 1,
    title: t('tournament.games.prime.csgo.title'),
    date: t('tournament.games.prime.csgo.date'),
    startTime: t('tournament.games.prime.csgo.startTime'),
    endTime: t('tournament.games.prime.csgo.endTime'),
    description: t('tournament.games.prime.csgo.description'),
    image: CS2Bg,
    maxParticipants: t('tournament.games.prime.csgo.maxParticipants'),
    prizePool: t('tournament.games.prime.csgo.prizePool'),
    category: 'prime',
    rules: tm('tournament.games.prime.csgo.rules'),
    inGame: tm('tournament.games.prime.csgo.inGame'),
    customization: tm('tournament.games.prime.csgo.customization'),
    gameclient: tm('tournament.games.prime.csgo.gameclient'),
    schedule: tm('tournament.games.prime.csgo.schedule'),
    prizes: tm('tournament.games.prime.csgo.prizes'),
    scoring: tm('tournament.games.prime.csgo.scoring'),
    urlSignUp: t('tournament.games.prime.csgo.urlSignUp'),
    urlBracket: t('tournament.games.prime.csgo.urlBracket'),
    registrationOpen: true
  },
  {
    id: 2,
    title: t('tournament.games.prime.lol.title'),
    date: t('tournament.games.prime.lol.date'),
    startTime: t('tournament.games.prime.lol.startTime'),
    endTime: t('tournament.games.prime.lol.endTime'),
    description: t('tournament.games.prime.lol.description'),
    image: lolGrandBg,
    maxParticipants: t('tournament.games.prime.lol.maxParticipants'),
    prizePool: t('tournament.games.prime.lol.prizePool'),
    category: 'prime',
    rules: tm('tournament.games.prime.lol.rules'),
    inGame: tm('tournament.games.prime.lol.inGame'),
    customization: tm('tournament.games.prime.lol.customization'),
    gameclient: tm('tournament.games.prime.lol.gameclient'),
    schedule: tm('tournament.games.prime.lol.schedule'),
    prizes: tm('tournament.games.prime.lol.prizes'),
    scoring: tm('tournament.games.prime.lol.scoring'),
    urlSignUp: t('tournament.games.prime.lol.urlSignUp'),
    urlBracket: t('tournament.games.prime.lol.urlBracket'),
    registrationOpen: false
  },
  {
    id: 3,
    title: t('tournament.games.core.rocketLeague.title'),
    date: t('tournament.games.core.rocketLeague.date'),
    startTime: t('tournament.games.core.rocketLeague.startTime'),
    endTime: t('tournament.games.core.rocketLeague.endTime'),
    description: t('tournament.games.core.rocketLeague.description'),
    image: rocketLeagueBg,
    maxParticipants: t('tournament.games.core.rocketLeague.maxParticipants'),
    prizePool: t('tournament.games.core.rocketLeague.prizePool'),
    category: 'core',
    rules: tm('tournament.games.core.rocketLeague.rules'),
    inGame: tm('tournament.games.core.rocketLeague.inGame'),
    customization: tm('tournament.games.core.rocketLeague.customization'),
    gameclient: tm('tournament.games.core.rocketLeague.gameclient'),
    schedule: tm('tournament.games.core.rocketLeague.schedule'),
    prizes: tm('tournament.games.core.rocketLeague.prizes'),
    scoring: tm('tournament.games.core.rocketLeague.scoring'),
    urlSignUp: t('tournament.games.core.rocketLeague.urlSignUp'),
    urlBracket: t('tournament.games.core.rocketLeague.urlBracket'),
    registrationOpen: true
  },
  {
    id: 4,
    title: t('tournament.games.core.marvelRival.title'),
    date: t('tournament.games.core.marvelRival.date'),
    startTime: t('tournament.games.core.marvelRival.startTime'),
    endTime: t('tournament.games.core.marvelRival.endTime'),
    description: t('tournament.games.core.marvelRival.description'),
    image: marvelRivalBg,
    maxParticipants: t('tournament.games.core.marvelRival.maxParticipants'),
    prizePool: t('tournament.games.core.marvelRival.prizePool'),
    category: 'core',
    rules: tm('tournament.games.core.marvelRival.rules'),
    inGame: tm('tournament.games.core.marvelRival.inGame'),
    customization: tm('tournament.games.core.marvelRival.customization'),
    gameclient: tm('tournament.games.core.marvelRival.gameclient'),
    schedule: tm('tournament.games.core.marvelRival.schedule'),
    prizes: tm('tournament.games.core.marvelRival.prizes'),
    scoring: tm('tournament.games.core.marvelRival.scoring'),
    urlSignUp: t('tournament.games.core.marvelRival.urlSignUp'),
    urlBracket: t('tournament.games.core.marvelRival.urlBracket'),
    registrationOpen: true
  },
  {
    id: 5,
    title: t('tournament.games.core.lol.title'),
    date: t('tournament.games.core.lol.date'),
    startTime: t('tournament.games.core.lol.startTime'),
    endTime: t('tournament.games.core.lol.endTime'),
    description: t('tournament.games.core.lol.description'),
    image: lolNormalBg,
    maxParticipants: t('tournament.games.core.lol.maxParticipants'),
    prizePool: t('tournament.games.core.lol.prizePool'),
    category: 'core',
    rules: tm('tournament.games.core.lol.rules'),
    inGame: tm('tournament.games.core.lol.inGame'),
    customization: tm('tournament.games.core.lol.customization'),
    gameclient: tm('tournament.games.core.lol.gameclient'),
    schedule: tm('tournament.games.core.lol.schedule'),
    prizes: tm('tournament.games.core.lol.prizes'),
    scoring: tm('tournament.games.core.lol.scoring'),
    urlSignUp: t('tournament.games.core.lol.urlSignUp'),
    urlBracket: t('tournament.games.core.lol.urlBracket'),
    registrationOpen: false
  },
  {
    id: 6,
    title: t('tournament.games.core.csgo.title'),
    date: t('tournament.games.core.csgo.date'),
    startTime: t('tournament.games.core.csgo.startTime'),
    endTime: t('tournament.games.core.csgo.endTime'),
    description: t('tournament.games.core.csgo.description'),
    image: CS2CoreBG,
    maxParticipants: t('tournament.games.core.csgo.maxParticipants'),
    prizePool: t('tournament.games.core.csgo.prizePool'),
    category: 'core',
    rules: tm('tournament.games.core.csgo.rules'),
    inGame: tm('tournament.games.core.csgo.inGame'),
    customization: tm('tournament.games.core.csgo.customization'),
    gameclient: tm('tournament.games.core.csgo.gameclient'),
    schedule: tm('tournament.games.core.csgo.schedule'),
    prizes: tm('tournament.games.core.csgo.prizes'),
    scoring: tm('tournament.games.core.csgo.scoring'),
    urlSignUp: t('tournament.games.core.csgo.urlSignUp'),
    urlBracket: t('tournament.games.core.csgo.urlBracket'),
    registrationOpen: true
  },
  {
    id: 7,
    title: t('tournament.games.solo.lol.title'),
    date: t('tournament.games.solo.lol.date'),
    startTime: t('tournament.games.solo.lol.startTime'),
    endTime: t('tournament.games.solo.lol.endTime'),
    description: t('tournament.games.solo.lol.description'),
    image: lolSoloBg,
    maxParticipants: t('tournament.games.solo.lol.maxParticipants'),
    prizePool: t('tournament.games.solo.lol.prizePool'),
    category: 'solo',
    rules: tm('tournament.games.solo.lol.rules'),
    inGame: tm('tournament.games.solo.lol.inGame'),
    customization: tm('tournament.games.solo.lol.customization'),
    gameclient: tm('tournament.games.solo.lol.gameclient'),
    schedule: tm('tournament.games.solo.lol.schedule'),
    prizes: tm('tournament.games.solo.lol.prizes'),
    scoring: tm('tournament.games.solo.lol.scoring'),
    urlSignUp: t('tournament.games.solo.lol.urlSignUp'),
    urlBracket: t('tournament.games.solo.lol.urlBracket'),
    registrationOpen: true
  },
  {
    id: 8,
    title: t('tournament.games.solo.rocketLeague.title'),
    date: t('tournament.games.solo.rocketLeague.date'),
    startTime: t('tournament.games.solo.rocketLeague.startTime'),
    endTime: t('tournament.games.solo.rocketLeague.endTime'),
    description: t('tournament.games.solo.rocketLeague.description'),
    image: rocketLeagueSoloBg,
    maxParticipants: t('tournament.games.solo.rocketLeague.maxParticipants'),
    prizePool: t('tournament.games.solo.rocketLeague.prizePool'),
    category: 'solo',
    rules: tm('tournament.games.solo.rocketLeague.rules'),
    inGame: tm('tournament.games.solo.rocketLeague.inGame'),
    customization: tm('tournament.games.solo.rocketLeague.customization'),
    gameclient: tm('tournament.games.solo.rocketLeague.gameclient'),
    schedule: tm('tournament.games.solo.rocketLeague.schedule'),
    prizes: tm('tournament.games.solo.rocketLeague.prizes'),
    scoring: tm('tournament.games.solo.rocketLeague.scoring'),
    urlSignUp: t('tournament.games.solo.rocketLeague.urlSignUp'),
    urlBracket: t('tournament.games.solo.rocketLeague.urlBracket'),
    registrationOpen: true
  },
  {
    id: 9,
    title: t('tournament.games.solo.tft.title'),
    date: t('tournament.games.solo.tft.date'),
    startTime: t('tournament.games.solo.tft.startTime'),
    endTime: t('tournament.games.solo.tft.endTime'),
    description: t('tournament.games.solo.tft.description'),
    image: tftBg,
    maxParticipants: t('tournament.games.solo.tft.maxParticipants'),
    prizePool: t('tournament.games.solo.tft.prizePool'),
    category: 'solo',
    rules: tm('tournament.games.solo.tft.rules'),
    inGame: tm('tournament.games.solo.tft.inGame'),
    customization: tm('tournament.games.solo.tft.customization'),
    gameclient: tm('tournament.games.solo.tft.gameclient'),
    schedule: tm('tournament.games.solo.tft.schedule'),
    prizes: tm('tournament.games.solo.tft.prizes'),
    scoring: tm('tournament.games.solo.tft.scoring'),
    urlSignUp: t('tournament.games.solo.tft.urlSignUp'),
    urlBracket: t('tournament.games.solo.tft.urlBracket'),
    registrationOpen: false
  },
  {
    id: 10,
    title: t('tournament.games.solo.trackMania.title'),
    date: t('tournament.games.solo.trackMania.date'),
    startTime: t('tournament.games.solo.trackMania.startTime'),
    endTime: t('tournament.games.solo.trackMania.endTime'),
    description: t('tournament.games.solo.trackMania.description'),
    image: trackManiaBg,
    maxParticipants: t('tournament.games.solo.trackMania.maxParticipants'),
    prizePool: t('tournament.games.solo.trackMania.prizePool'),
    category: 'solo',
    maps: tm('tournament.games.solo.trackMania.maps'),
    rules: tm('tournament.games.solo.trackMania.rules'),
    inGame: tm('tournament.games.solo.trackMania.inGame'),
    customization: tm('tournament.games.solo.trackMania.customization'),
    gameclient: tm('tournament.games.solo.trackMania.gameclient'),
    schedule: tm('tournament.games.solo.trackMania.schedule'),
    prizes: tm('tournament.games.solo.trackMania.prizes'),
    scoring: tm('tournament.games.solo.trackMania.scoring'),
    urlSignUp: t('tournament.games.solo.trackMania.urlSignUp'),
    urlBracket: t('tournament.games.solo.trackMania.urlBracket'),
    registrationOpen: true
  },
  {
    id: 11,
    title: t('tournament.games.fun.chickenNugget.title'),
    date: t('tournament.games.fun.chickenNugget.date'),
    startTime: t('tournament.games.fun.chickenNugget.startTime'),
    endTime: t('tournament.games.fun.chickenNugget.endTime'),
    description: t('tournament.games.fun.chickenNugget.description'),
    image: chickenNuggetBg,
    maxParticipants: t('tournament.games.fun.chickenNugget.maxParticipants'),
    prizePool: t('tournament.games.fun.chickenNugget.prizePool'),
    category: 'fun',
    rules: tm('tournament.games.fun.chickenNugget.rules'),
    inGame: tm('tournament.games.fun.chickenNugget.inGame'),
    customization: tm('tournament.games.fun.chickenNugget.customization'),
    gameclient: tm('tournament.games.fun.chickenNugget.gameclient'),
    schedule: tm('tournament.games.fun.chickenNugget.schedule'),
    prizes: tm('tournament.games.fun.chickenNugget.prizes'),
    scoring: tm('tournament.games.fun.chickenNugget.scoring'),
    urlSignUp: t('tournament.games.fun.chickenNugget.urlSignUp'),
    urlBracket: t('tournament.games.fun.chickenNugget.urlBracket'),
    registrationOpen: true
  },
  {
    id: 12,
    title: t('tournament.games.fun.chess.title'),
    date: t('tournament.games.fun.chess.date'),
    startTime: t('tournament.games.fun.chess.startTime'),
    endTime: t('tournament.games.fun.chess.endTime'),
    description: t('tournament.games.fun.chess.description'),
    image: chessBg,
    maxParticipants: t('tournament.games.fun.chess.maxParticipants'),
    prizePool: t('tournament.games.fun.chess.prizePool'),
    category: 'fun',
    rules: tm('tournament.games.fun.chess.rules'),
    inGame: tm('tournament.games.fun.chess.inGame'),
    customization: tm('tournament.games.fun.chess.customization'),
    gameclient: tm('tournament.games.fun.chess.gameclient'),
    schedule: tm('tournament.games.fun.chess.schedule'),
    prizes: tm('tournament.games.fun.chess.prizes'),
    scoring: tm('tournament.games.fun.chess.scoring'),
    urlSignUp: t('tournament.games.fun.chess.urlSignUp'),
    urlBracket: t('tournament.games.fun.chess.urlBracket'),
    registrationOpen: true
  },
  {
    id: 13,
    title: t('tournament.games.fun.quiz.title'),
    date: t('tournament.games.fun.quiz.date'),
    startTime: t('tournament.games.fun.quiz.startTime'),
    endTime: t('tournament.games.fun.quiz.endTime'),
    description: t('tournament.games.fun.quiz.description'),
    image: quizBg,
    maxParticipants: t('tournament.games.fun.quiz.maxParticipants'),
    prizePool: t('tournament.games.fun.quiz.prizePool'),
    category: 'fun',
    rules: tm('tournament.games.fun.quiz.rules'),
    disclaimer: tm('tournament.games.fun.quiz.disclaimer'),
    inGame: tm('tournament.games.fun.quiz.inGame'),
    customization: tm('tournament.games.fun.quiz.customization'),
    gameclient: tm('tournament.games.fun.quiz.gameclient'),
    schedule: tm('tournament.games.fun.quiz.schedule'),
    prizes: tm('tournament.games.fun.quiz.prizes'),
    scoring: tm('tournament.games.fun.quiz.scoring'),
    urlSignUp: t('tournament.games.fun.quiz.urlSignUp'),
    urlBracket: t('tournament.games.fun.quiz.urlBracket'),
    registrationOpen: true
  }
])

</script>

<style scoped>
.tournament-wrapper {
  width: 100%;
  min-height: calc(100vh - 150px);
  position: relative;
}

.tournament-container {
  position: relative;
  z-index: 1;
}

.tournament-page {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.tournament-page.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 4rem 0 2rem 0;
  margin-top: 2rem;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

@media (max-width: 768px) {
  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }
}
</style>